import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, ArrowRight, History, Pencil, Plus, Search, Trash2, Workflow } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { AutomationRuns } from '@/components/AutomationRuns';
import { Metric, PageIntro, PageTabs, QueryError, Status, count } from '@/workspace/ui';
import './automation-controls.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function timeAgo(ts) {
  if (!ts) return null;
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const TRIGGER = { lead_identified: 'New lead trigger', purchase: 'Purchase trigger', purchase_any: 'Any purchase trigger', renewal: 'Renewal trigger', refund: 'Refund trigger' };

function AutomationCard({ auto, onEdit, onDelete, onToggle, onViewRuns }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const deletingRef = useRef(false);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const updatingRef = useRef(false);
  const handleToggle = async (enabled) => {
    if (deletingRef.current || updatingRef.current) return;
    updatingRef.current = true;
    setUpdating(true);
    setUpdateError('');
    try {
      if (!await onToggle(auto.id, enabled)) setUpdateError('Could not update this automation. Try again.');
    } finally {
      updatingRef.current = false;
      setUpdating(false);
    }
  };
  const handleDelete = async (event) => {
    event.preventDefault();
    if (deletingRef.current || updatingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    setDeleteError('');
    try {
      if (await onDelete(auto.id)) setConfirmDelete(false);
      else setDeleteError('Could not delete this automation. Try again.');
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };
  const filterCount = auto.filters?.length || 0;
  // Support both new actions[] and legacy field_map
  const actionCount = auto.actions?.length || (auto.webhook_url ? 1 : 0);
  const totalMapped = auto.actions?.reduce((s, a) => s + (a.field_map?.length || 0), 0)
                   || auto.field_map?.length || 0;
  // Display URL from first action or legacy webhook_url
  const steps = auto.steps || [];
  const displayUrl  = steps.find(st => st.type === 'webhook')?.config?.url || auto.actions?.[0]?.webhook_url || auto.webhook_url
    || (steps.some(st => st.type === 'capi') ? 'Facebook Conversions API' : steps.some(st => st.type === 'ghl') ? 'GoHighLevel' : '');

  return (
    <article className="sp-automation">
      <span className="sp-automation-icon"><Workflow size={20} /></span>
      <div className="sp-automation-main">
        <div className="sp-automation-title"><h3>{auto.name}</h3><Status tone={auto.enabled ? 'green' : 'quiet'}>{updating ? 'Updating…' : auto.enabled ? 'Active' : 'Paused'}</Status></div>
        {updateError && <p role="alert" className="sp-automation-error">{updateError}</p>}
        <p data-testid={`automation-trigger-badge-${auto.id}`}>
          {TRIGGER[auto.trigger] || TRIGGER.lead_identified} <ArrowRight size={13} /> <span className="sp-automation-url">{displayUrl || 'No webhook yet'}</span>
        </p>
        <div className="sp-automation-meta">
          {filterCount > 0 && <span>{filterCount} filter{filterCount !== 1 ? 's' : ''}</span>}
          {steps.length > 0 && <span>{steps.length} step{steps.length !== 1 ? 's' : ''}</span>}
          {actionCount > 1 && <span>{actionCount} webhook steps</span>}
          {totalMapped > 0 && <span>{totalMapped} field{totalMapped !== 1 ? 's' : ''} mapped</span>}
          <span>{count(auto.trigger_count || 0)} runs</span>
          <span>{auto.last_triggered_at ? `Last fired ${timeAgo(auto.last_triggered_at)}` : 'Never fired'}</span>
        </div>
      </div>
      <div className="sp-automation-actions">
        <button type="button" className="btn" onClick={() => onViewRuns(auto)}>
          <History size={14} /> Runs{auto.trigger_count > 0 && <span className="sp-button-count">{count(auto.trigger_count)}</span>}
        </button>
        <button type="button" className="btn" disabled={deleting || updating} onClick={() => onEdit(auto)}><Pencil size={14} /> Edit</button>
        <AlertDialog open={confirmDelete} onOpenChange={open => { if (!deletingRef.current) { setConfirmDelete(open); setDeleteError(''); } }}>
          <AlertDialogTrigger asChild>
            <button type="button" className="btn ghost icon-btn sp-danger-button" disabled={deleting || updating} aria-label={`Delete ${auto.name}`}><Trash2 size={15} /></button>
          </AlertDialogTrigger>
          <AlertDialogContent className="sp-dialog sp-automation-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Automation?</AlertDialogTitle>
              <AlertDialogDescription>"{auto.name}" will be permanently deleted.</AlertDialogDescription>
            </AlertDialogHeader>
            {deleteError && <p className="sp-automation-error" role="alert">{deleteError}</p>}
            <AlertDialogFooter>
              <AlertDialogCancel className="btn" disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction className="sp-danger-solid" disabled={deleting} aria-busy={deleting} onClick={handleDelete}>{deleting ? 'Deleting…' : 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <label className="sp-switch">
          <input type="checkbox" checked={!!auto.enabled} disabled={deleting || updating} aria-busy={updating} onChange={e => handleToggle(e.target.checked)}
            aria-label={`${auto.enabled ? 'Pause' : 'Activate'} ${auto.name}`} /><span />
        </label>
      </div>
    </article>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="empty-state">
      <Workflow size={28} aria-hidden="true" />
      <h3>No automations yet</h3>
      <p>Automatically send lead data to your CRM, email platform, or any webhook when a contact is identified.</p>
      <button type="button" className="btn primary" onClick={onNew}><Plus size={15} /> Create your first automation</button>
    </div>
  );
}

// Automations — design: the prototype's Automations page (workflow library), on the live automations API.
export default function AutomationsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [runsAuto,    setRunsAuto]    = useState(null);

  const { data: automations = [], isLoading: loading, isError, refetch } = useQuery({
    queryKey: ['automations'],
    queryFn: () => fetch(`${API}/automations`).then(r => {
      if (!r.ok) throw new Error('Failed');
      return r.json();
    }),
  });

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/automations/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      await qc.invalidateQueries({ queryKey: ['automations'] });
      toast.success('Automation deleted');
      return true;
    } catch { toast.error('Failed to delete'); return false; }
  };

  const handleToggle = async (id, enabled) => {
    try {
      const res = await fetch(`${API}/automations/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error();
      await qc.invalidateQueries({ queryKey: ['automations'] });
      toast.success(enabled ? 'Automation activated' : 'Automation paused');
      return true;
    } catch { toast.error('Failed to update'); return false; }
  };

  const visible = automations.filter(a => a.name?.toLowerCase().includes(search.toLowerCase()) && (filter === 'all' || !!a.enabled === (filter === 'active')));
  const activeCount = automations.filter(a => a.enabled).length;

  const handleNewAutomation = () => navigate('/automations/new');
  const handleEditAutomation = (auto) => navigate(`/automations/builder/${auto.id}`);

  const totalRuns = automations.reduce((s, a) => s + (a.trigger_count || 0), 0);

  return <>
    <PageIntro title="Automations"
      actions={<button type="button" className="btn primary" onClick={handleNewAutomation} data-testid="new-automation-button"><Plus size={17} /> New automation</button>} />
    <div className="metrics metrics-two">
      <Metric icon={Workflow} label="Active" value={loading || isError ? '—' : count(activeCount)} detail={`of ${count(automations.length)} automations`} />
      <Metric icon={Activity} label="Runs" value={loading || isError ? '—' : count(totalRuns)} detail="all time" tone="aqua" />
    </div>
    <section className="panel sp-workflow-library">
      <div className="table-toolbar"><PageTabs label="Automation status" active={filter} onChange={setFilter} tabs={[{ id: 'all', label: 'All workflows', count: automations.length }, { id: 'active', label: 'Active', count: activeCount }, { id: 'paused', label: 'Paused', count: automations.length - activeCount }]} /><label className="search-input"><Search size={16} /><span className="sr-only">Search workflows</span><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search workflows…" /></label></div>
      {isError ? <QueryError onRetry={refetch} /> : loading ? <div className="empty-state">Loading automations…</div> : automations.length === 0 ? <EmptyState onNew={handleNewAutomation} /> : (
        <div className="sp-automation-list">
          {visible.length === 0 && <div className="empty-state">No workflows match these filters.</div>}
          {visible.map(auto => (
            <AutomationCard key={auto.id} auto={auto}
              onEdit={handleEditAutomation}
              onDelete={handleDelete}
              onToggle={handleToggle}
              onViewRuns={a => setRunsAuto(a)}
            />
          ))}
        </div>
      )}
    </section>
    <AutomationRuns open={!!runsAuto} automation={runsAuto} onClose={() => setRunsAuto(null)} />
  </>;
}
